// src/components/Sidebar.jsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Home, Heart, Brain, Moon, Mic, Users, Star, BrainCircuit, HeartHandshake,
  Briefcase, Settings,
  ChevronDown, ChevronRight, Lock, X, LogOut, Shield,
  Compass, Target, ClipboardList, Wallet,
  Trophy, Award, Flag, ShieldCheck, BookOpen, Megaphone, Eye } from 'lucide-react';
import { copyFor, isCorporateOrg } from '../data/copyDictionary';
import { resolveSport } from '../data/sports';
import { getEdition, resolveEditionId } from '../data/editions';
import Logo from './branding/Logo';
import BRAND_CONFIG from '../config/brand';
import { NAV_TOPIC_GROUPS, TEEN_EXCLUDED_TOPIC_IDS } from '../data/navTopics';
import { allowedSubTopics, applyYouthFloor } from '../utils/navAccess';
import { resolveAgeTier } from '../utils/ageTier';
import { customSections, customGroupLabel } from '../utils/customNav';

// Icon names an org may choose for a custom section (CUSTOM_NAV_ICONS).
const CUSTOM_ICON_MAP = { Trophy, Award, Flag, ShieldCheck, Users, HeartHandshake, Star, BookOpen, Megaphone };

const Sidebar = ({ 
    activeTab = 'dashboard', 
    navigateTo, 
    isOpen, 
    setIsOpen, 
    isAdmin, // True capability
    isSuperAdmin,
    surface, // resolved member-funnel surface (athlete/coach/executive/family/teen/kidz)
    onOpenAdmin,
    onLogout,
    currentUser = { name: "Guest" },
    currentOrg,
    onViewAs = null // an organization's admin: open the hub as each kind of member sees it
}) => {
  const [expandedMenus, setExpandedMenus] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState([]); 
  const [dynamicNav, setDynamicNav] = useState(null); 

  // --- THE MAGIC TOGGLE ---
  const isCorporate = isCorporateOrg(currentOrg);
  const t = useCallback((key) => copyFor(key, isCorporate), [isCorporate]);

  // --- TERMINOLOGY (single source: data/copyDictionary.js) ---
  const DICT = {
      roleUser: t('roleUser'),
      roleManager: t('roleManager'),
      roleExec: t('roleExec'),
      groupManager: t('groupManager'),
      groupUser: t('groupUser'),
      groupDaily: t('groupDaily')
  };

  // --- Auto-collapse lower tiers for Executives to keep UI clean
  useEffect(() => {
      const isViewAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
      if (isViewAdmin) {
          setCollapsedGroups([DICT.groupManager, DICT.groupUser]);
      } else {
          setCollapsedGroups([]);
      }
  }, [currentUser?.role, DICT.groupManager, DICT.groupUser]);

  const branding = currentOrg?.branding || {};
  const isBrandingEnabled = branding.enabled;

  // HUB THEME — the white-label variant: a light card sidebar with the brand
  // colour reserved for the active item, the client's own hub name, and a
  // "Powered by EAS" credit. Classic stays the coloured-sidebar look. Both are
  // driven from the same token object so the render below never branches on
  // colour, only on the handful of structural differences.
  const isHub = isBrandingEnabled && branding.theme === 'hub';

  const customStyles = isHub ? {
      sidebarBg: '#ffffff',
      textColor: '#334155',
      activeBg: branding.primaryColor || '#0679af',
      activeTextColor: '#ffffff',
      activeIconColor: branding.accentColor || '#ffffff',
      accentColor: branding.accentColor || '#0ea5e9',
      borderColor: 'rgba(15,23,42,0.08)',
      subActiveBg: 'rgba(15,23,42,0.06)',
      footerTileBg: 'rgba(15,23,42,0.03)'
  } : {
      sidebarBg: isBrandingEnabled ? (branding.darkColor || '#0f172a') : undefined,
      textColor: isBrandingEnabled ? (branding.sidebarTextColor || '#ffffff') : undefined,
      activeBg: isBrandingEnabled ? (branding.primaryColor || '#0679af') : undefined,
      activeTextColor: isBrandingEnabled ? (branding.sidebarTextColor || '#ffffff') : undefined,
      activeIconColor: isBrandingEnabled ? (branding.sidebarTextColor || '#ffffff') : undefined,
      accentColor: isBrandingEnabled ? (branding.accentColor || '#0ea5e9') : undefined,
      borderColor: isBrandingEnabled ? 'rgba(255,255,255,0.1)' : undefined,
      subActiveBg: 'rgba(255,255,255,0.1)',
      footerTileBg: 'rgba(0,0,0,0.2)'
  };

  // Your original Nav, magically renaming specific items for corporate
  const DEFAULT_NAV = useMemo(() => [
    { id: 'dashboard', label: t('navDashboard'), icon: <Home size={20} /> },
    { id: 'journal', label: 'My Journal', icon: <ClipboardList size={20} /> },
    { 
      id: 'mental-health', label: t('navMentalHealth'), icon: <Heart size={20} />,
      subItems: NAV_TOPIC_GROUPS['mental-health']
    },
    { 
      id: 'mental-performance', label: t('navMentalPerformance'), icon: <Brain size={20} />,
      subItems: NAV_TOPIC_GROUPS['mental-performance']
    },
    { id: 'sleep', label: 'Sleep Optimization', icon: <Moon size={20} /> },
    { id: 'reset-recharge', label: 'Reset & Recharge', icon: <HeartHandshake size={20} /> },
    // Partner-authored (Scotia). Org-gated below on features.enableFinanceHub.
    { id: 'finance', label: 'Financial Wellbeing', icon: <Wallet size={20} /> },
    { id: 'performance-123', label: t('navPerformance123'), icon: <Mic size={20} /> },
    { id: 'family-huddle', label: t('navFamilyHuddle'), icon: <Users size={20} /> },
    { id: 'kidzcorner', label: t('navKidz'), icon: <Star size={20} /> },
    { id: 'leadership', label: 'Leadership Hub', icon: <Briefcase size={20} /> },
    { id: 'brain-health', label: 'Brain Health', icon: <BrainCircuit size={20} /> },
    { id: 'transition', label: 'Transition', icon: <Compass size={20} /> }
    // TODO: restore when career content is authored (topic currently has no resources —
    // hidden so users aren't led to a dead topic). Re-add the ArrowRightCircle import too.
    // { id: 'career', label: 'Career Growth', icon: <ArrowRightCircle size={20} /> }
  ], [t]);

  useEffect(() => {
      if (currentOrg && currentOrg.navConfig) {
          const savedConfig = currentOrg.navConfig;
          const reorderedItems = savedConfig
            .filter(item => item.isVisible !== false) 
            .map(configItem => {
              const originalItem = DEFAULT_NAV.find(i => i.id === configItem.id);
              if (!originalItem) return null; 
              return { ...originalItem, label: configItem.label || originalItem.label }; 
            })
            .filter(Boolean);

          const savedIds = new Set(savedConfig.map(i => i.id));
          const missingItems = DEFAULT_NAV.filter(i => !savedIds.has(i.id));

          // Narrow each section to the sub-topics this org allows (navConfig
          // entry.subItems). A section left with none is dropped - an empty
          // dropdown is a dead end. utils/navAccess.js enforces the same list
          // at the router, so a hidden topic is unreachable, not just unlisted.
          const scoped = [...reorderedItems, ...missingItems]
              .map(item => (NAV_TOPIC_GROUPS[item.id] ? { ...item, subItems: allowedSubTopics(currentOrg, item.id) } : item))
              .filter(item => !NAV_TOPIC_GROUPS[item.id] || item.subItems.length > 0);

          setDynamicNav(scoped);
      } else {
          setDynamicNav(DEFAULT_NAV);
      }
  }, [currentOrg, DEFAULT_NAV]);

  // ORG-DEFINED SECTIONS (utils/customNav.js). Rendered as their own group under
  // the org's name, so a client's own content reads as theirs and never gets
  // filed under a platform heading like "Resource Libraries".
  const customNavItems = customSections(currentOrg).map(section => {
      const SectionIcon = CUSTOM_ICON_MAP[section.icon] || Trophy;
      return { id: section.id, label: section.label, icon: <SectionIcon size={20} />, subItems: section.subItems };
  });
  const customGroupTitle = customGroupLabel(currentOrg);
  const memberTier = resolveAgeTier(currentUser);

  const baseMenu = dynamicNav || DEFAULT_NAV;
  // The youth floor (utils/navAccess.js): what an under-18 never sees, applied
  // to the menu here and to the router in App.jsx from the same function.
  let displayMenu = applyYouthFloor([...baseMenu], currentOrg, memberTier);
  // A youth-sport parent gets the org's full member menu (Sleep, Brain Health,
  // Rest & Recharge, the topics, the journal, the org's own sections), with
  // their hub as home. Kidz Corner is the one thing that is not theirs.
  if (surface === 'parent') {
      displayMenu = displayMenu
          .filter(i => i.id !== 'kidzcorner')
          .map(i => (i.id === 'dashboard' ? { ...i, label: 'Parent Hub', icon: <Users size={20} /> } : i));
  }

  const isSquadHubEnabled = currentOrg?.features?.enableSquadHub === true;
  const isCoach = currentUser?.role === 'coach';

  const isViewAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

  // Single-page member surfaces (spouse/teen/kidz) have no sub-routes — the
  // athlete nav doesn't apply to them. Give each a minimal, self-describing nav
  // entry instead of the (inert) athlete links. The footer (profile/logout) is
  // unaffected, so these users keep a way to sign out.
  // Standard content dropdowns surfaced on the member hubs. Mental Performance is
  // shared in full; Mental Health is age-curated — the teen list intentionally
  // omits Addictions & Gambling (adult-oriented). This is enforced again at the
  // routing layer in App.jsx so a teen can't reach them via a direct link either.
  const MP_SUB = allowedSubTopics(currentOrg, 'mental-performance', memberTier);
  const FULL_MH_SUB = allowedSubTopics(currentOrg, 'mental-health', memberTier);
  // The teen exclusions are a platform age gate: an org allowlist can narrow
  // this list further, never widen it.
  const TEEN_MH_SUB = FULL_MH_SUB.filter(t => !TEEN_EXCLUDED_TOPIC_IDS.includes(t.id));
  // Each returns an array so a section with no allowed sub-topics simply
  // contributes nothing when spread into a group.
  const mhItem = (sub) => (sub.length ? [{ id: 'mental-health', label: 'Mental Health', icon: <Heart size={20} />, subItems: sub }] : []);
  const mpItem = (label) => (MP_SUB.length ? [{ id: 'mental-performance', label, icon: <Brain size={20} />, subItems: MP_SUB }] : []);

  // Financial Wellbeing (partner-authored) is org-gated; the hub itself picks
  // age-appropriate content from the member's tier, so family and teen share
  // one nav entry with different labels.
  const isFinanceEnabled = currentOrg?.features?.enableFinanceHub === true;
  const financeItem = (label) => (isFinanceEnabled ? [{ id: 'finance', label, icon: <Wallet size={20} /> }] : []);

  const MEMBER_SURFACE_NAV = {
      family: {
          groupLabel: 'Your Hub', item: { id: 'dashboard', label: 'Family & Partner Hub', icon: <Users size={20} /> },
          topicGroups: [{ title: 'Explore', items: [...mhItem(FULL_MH_SUB), ...mpItem('Mental Fitness'), ...financeItem('Financial Wellbeing')] }]
      },
      teen: {
          groupLabel: 'Your Hub', item: { id: 'dashboard', label: 'Teen Hub', icon: <Compass size={20} /> },
          topicGroups: [{ title: 'Explore', items: [...mhItem(TEEN_MH_SUB), ...mpItem('Mental Fitness'), ...financeItem('Money')] }]
      },
      kidz:   { groupLabel: 'Your Hub', item: { id: 'dashboard', label: 'Kidz Corner', icon: <Star size={20} /> } },
  };
  const memberSurfaceNav = MEMBER_SURFACE_NAV[surface];

  // --- 1. STRICT ROLE FILTERING ---
  if (!isViewAdmin) {
      displayMenu = displayMenu.filter(i => i.id !== 'admin');
  }
  if (!isViewAdmin && !isCoach) {
      displayMenu = displayMenu.filter(i => i.id !== 'leadership' && i.id !== 'squad-dashboard');
  }
  if (!isFinanceEnabled) {
      displayMenu = displayMenu.filter(i => i.id !== 'finance');
  }

  // --- 2. INJECT COACH TOOLS ---
  if ((isCoach || isViewAdmin) && isSquadHubEnabled) {
    if (!displayMenu.find(i => i.id === 'squad-dashboard')) {
        const dashIdx = displayMenu.findIndex(i => i.id === 'dashboard');
        const insertIdx = dashIdx >= 0 ? dashIdx + 1 : 1;
        displayMenu.splice(insertIdx, 0, { 
            id: 'squad-dashboard', 
            label: t('navSquadPlaybook'), 
            icon: <Target size={20} /> 
        });
    }
  }

  // --- 3. NESTING DOLL GROUPING ---
  let groupedNav = {};
  const CORE_ACTIONS = ['dashboard']; // Removed 'recovery' here
  const COACH_VIEWS = ['squad-dashboard', 'leadership'];
  const TOOLKITS = ['performance-123', 'family-huddle', 'kidzcorner'];

  if (isViewAdmin) {
      // Workforce Analytics is a page of its own: the console swaps in its own
      // sidebar (enterprise/ConsoleSidebar.jsx) with the tabs (consoleTabs.js).
      const EXEC_NAV = [
          { id: 'command-center', label: getEdition(resolveEditionId(currentOrg)).consoleLabel || t('navExecInsights'), icon: <BrainCircuit size={20} /> }
      ];

      groupedNav = {
          'Enterprise Control': [
              { id: 'admin', label: 'System Admin', icon: <Settings size={20} /> },
              ...EXEC_NAV
          ],
          [DICT.groupManager]: displayMenu.filter(i => COACH_VIEWS.includes(i.id)),
          [DICT.groupUser]: displayMenu.filter(i => !COACH_VIEWS.includes(i.id) && i.id !== 'admin')
      };

  } else if (isCoach) {
      groupedNav = {
          [DICT.groupManager]: displayMenu.filter(i => COACH_VIEWS.includes(i.id)),
          [DICT.groupDaily]: displayMenu.filter(i => CORE_ACTIONS.includes(i.id)),
          'Interactive Toolkits': displayMenu.filter(i => TOOLKITS.includes(i.id)),
          'Resource Libraries': displayMenu.filter(i => !CORE_ACTIONS.includes(i.id) && !TOOLKITS.includes(i.id) && !COACH_VIEWS.includes(i.id))
      };

  } else {
      groupedNav = {
          [DICT.groupDaily]: displayMenu.filter(i => CORE_ACTIONS.includes(i.id)),
          'Interactive Toolkits': displayMenu.filter(i => TOOLKITS.includes(i.id)),
          'Resource Libraries': displayMenu.filter(i => !CORE_ACTIONS.includes(i.id) && !TOOLKITS.includes(i.id) && !COACH_VIEWS.includes(i.id))
      };
  }

  const toggleGroup = (groupTitle) => {
      setCollapsedGroups(prev => 
          prev.includes(groupTitle) ? prev.filter(g => g !== groupTitle) : [...prev, groupTitle]
      );
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => prev.includes(menuId) ? prev.filter(id => id !== menuId) : [...prev, menuId]);
  };

  const handleNavClick = (id) => {
    if (navigateTo) navigateTo(id);
    if (setIsOpen) setIsOpen(false);
  };

  const renderNavGroup = (title, items) => {
      if (!items || items.length === 0) return null;
      
      const isGroupCollapsed = collapsedGroups.includes(title);

      return (
          <div className="mb-6">
              {title && (
                  <button 
                    onClick={() => toggleGroup(title)}
                    className="w-full flex items-center justify-between px-4 mb-2 opacity-50 hover:opacity-100 transition-opacity focus:outline-none"
                    style={{ color: customStyles.textColor || '#94a3b8' }}
                  >
                      <span className="text-[10px] font-black uppercase tracking-widest">{title}</span>
                      {isGroupCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                  </button>
              )}
              
              {!isGroupCollapsed && (
                  <div className="space-y-1.5 animate-in slide-in-from-top-2 duration-300">
                      {items.map((item) => {
                        const isActive = activeTab === item.id;
                        const isExpanded = expandedMenus.includes(item.id);
                        const hasSubItems = item.subItems?.length > 0;

                        const itemStyle = isActive && isBrandingEnabled ? {
                            backgroundColor: customStyles.activeBg,
                            color: customStyles.activeTextColor,
                            boxShadow: isHub ? undefined : '0 4px 12px rgba(0,0,0,0.1)'
                        } : {
                            color: customStyles.textColor,
                            opacity: isBrandingEnabled && !isHub ? 0.8 : 1
                        };

                        return (
                            <div key={item.id} className="animate-in fade-in slide-in-from-left-4 duration-300">
                                <button 
                                    onClick={() => hasSubItems ? toggleMenu(item.id) : handleNavClick(item.id)}
                                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all border border-transparent
                                        ${!isBrandingEnabled && isActive ? 'bg-brand-primary text-white shadow-lg' : ''}
                                        ${!isBrandingEnabled && !isActive ? 'text-slate-300 hover:bg-slate-800' : (isHub ? 'hover:bg-slate-100' : 'hover:bg-black/10')}
                                    `}
                                    style={itemStyle}
                                >
                                    <div className="flex items-center gap-3">
                                        {React.cloneElement(item.icon, {
                                            color: isBrandingEnabled ? (isActive ? customStyles.activeIconColor : customStyles.textColor) : undefined
                                        })}
                                        <span className="font-medium text-left">{item.label}</span>
                                    </div>
                                    {hasSubItems && (
                                        isExpanded ?
                                        <ChevronDown size={16} color={isActive ? customStyles.activeTextColor : customStyles.textColor} /> :
                                        <ChevronRight size={16} color={isActive ? customStyles.activeTextColor : customStyles.textColor} />
                                    )}
                                </button>
                                
                                {hasSubItems && isExpanded && (
                                    <div 
                                        className="ml-4 pl-4 border-l mt-1 space-y-1"
                                        style={{ borderColor: customStyles.borderColor || 'rgba(255,255,255,0.2)' }}
                                    >
                                        {item.subItems.map((subItem) => (
                                            <button 
                                                key={subItem.id} 
                                                onClick={() => handleNavClick(subItem.id)}
                                                className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-colors ${isHub ? 'hover:bg-slate-100' : 'hover:bg-white/5'}`}
                                                style={{
                                                    color: customStyles.textColor,
                                                    opacity: activeTab === subItem.id ? 1 : 0.7,
                                                    fontWeight: activeTab === subItem.id ? '800' : '400',
                                                    backgroundColor: activeTab === subItem.id ? customStyles.subActiveBg : 'transparent'
                                                }}
                                            >
                                                {subItem.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                      })}
                  </div>
              )}
          </div>
      );
  };

  return (
    <>
      <div 
        className={`fixed inset-0 z-[190] bg-black/50 transition-opacity lg:hidden ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} 
        onClick={() => setIsOpen && setIsOpen(false)} 
      />
      
      <div 
        className={`fixed lg:sticky top-0 left-0 z-[200] h-[100dvh] lg:h-screen overflow-y-auto no-scrollbar 
        transform transition-transform lg:translate-x-0 
        ${isOpen ? 'translate-x-0' : '-translate-x-full'} 
        flex flex-col w-64 pb-20 lg:pb-0 overflow-hidden
        ${isHub ? 'border-r border-slate-200' : 'shadow-2xl'}
        ${!isBrandingEnabled ? 'bg-brand-dark text-white' : ''}`}
        style={{ 
            backgroundColor: customStyles.sidebarBg, 
            color: customStyles.textColor 
        }}
      >
        
        {isBrandingEnabled && (
            <div className="absolute -bottom-10 -right-10 opacity-5 pointer-events-none z-0 transform rotate-12">
                <Logo size={300} style={{ color: customStyles.textColor }} />
            </div>
        )}

        {/* HEADER — one stacked lockup for both themes: the big logo, then the
            name, then the "Powered by EAS" credit. The Hub theme only swaps in the
            client's hub name and darkens the type for the light sidebar. */}
        <div
            className="p-6 flex flex-col items-center justify-center shrink-0 relative border-b z-10"
            style={{ borderColor: customStyles.borderColor || 'rgba(255,255,255,0.1)' }}
        >
          <div className="flex flex-col items-center gap-0 text-center w-full">
            <div className="w-24 h-24 flex items-center justify-center bg-transparent rounded-xl overflow-hidden transition-transform hover:scale-105 duration-500 mb-2">
              {branding.logoUrl ? (
                  <img src={branding.logoUrl} alt={currentOrg?.name || "Logo"} className="w-full h-full object-contain drop-shadow-md" />
              ) : (
                  <Logo size={80} style={{ color: customStyles.accentColor || 'white' }} />
              )}
            </div>

            <div className="flex flex-col items-center leading-none w-full">
              {isBrandingEnabled ? (
                  <>
                    <div className="text-lg tracking-wider font-black mt-1 text-center leading-tight uppercase" style={{ color: isHub ? '#0f172a' : customStyles.textColor }}>
                        {(isHub && branding.hubName) || currentOrg?.name}
                    </div>
                    <div className="flex items-center justify-center gap-1.5 mt-3 opacity-80" style={{ color: customStyles.textColor }}>
                        <span className="text-[9px] uppercase tracking-widest font-bold">Powered by</span>
                        <Logo size={24} style={{ color: customStyles.textColor }} />
                    </div>
                  </>
              ) : (
                  <>
                    <div className="text-2xl tracking-widest">
                        <span className="font-black text-white">{BRAND_CONFIG.appName.split(' ')[0]}</span>
                        <span className="font-light text-brand-accent ml-1">{BRAND_CONFIG.appName.split(' ')[1]}</span>
                    </div>
                    <span className="text-[9px] uppercase tracking-[0.3em] text-slate-500 font-bold mt-0.5">{BRAND_CONFIG.appSubtitle}</span>
                  </>
              )}
            </div>
          </div>
          <button onClick={() => setIsOpen && setIsOpen(false)} className="absolute top-4 right-4 lg:hidden opacity-70 hover:opacity-100">
            <X size={24} style={{ color: customStyles.textColor }} />
          </button>
        </div>

        <nav className="flex-1 px-4 py-6 z-10 relative">
          {memberSurfaceNav ? (
             <>
                {renderNavGroup(memberSurfaceNav.groupLabel, [memberSurfaceNav.item])}
                {/* The kids surface is a single self-contained page with no topic
                    routes, so an org's custom sections are not offered there. */}
                {surface !== 'kidz' && renderNavGroup(customGroupTitle, customNavItems)}
                {(memberSurfaceNav.topicGroups || []).map((g) => (
                    <React.Fragment key={g.title}>{renderNavGroup(g.title, g.items)}</React.Fragment>
                ))}
             </>
          ) : isViewAdmin ? (
             <>
                {onViewAs && (
                    <button
                        type="button"
                        onClick={onViewAs}
                        className="w-full mb-3 flex items-center gap-3 px-3 py-2.5 rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 text-left transition-colors"
                        title="See the hub as a player, a parent or a coach sees it"
                    >
                        <Eye size={18} className="shrink-0 opacity-80" />
                        <span className="min-w-0">
                            <span className="block text-sm font-bold leading-tight">View as a member</span>
                            <span className="block text-[11px] opacity-70 leading-tight">{copyFor('viewAsSub', isCorporateOrg(currentOrg))}</span>
                        </span>
                    </button>
                )}
                {renderNavGroup('Enterprise Control', groupedNav['Enterprise Control'])}
                {renderNavGroup(DICT.groupManager, groupedNav[DICT.groupManager])}
                {renderNavGroup(DICT.groupUser, groupedNav[DICT.groupUser])}
                {renderNavGroup(customGroupTitle, customNavItems)}
             </>
          ) : isCoach ? (
             <>
                {renderNavGroup(DICT.groupManager, groupedNav[DICT.groupManager])}
                {renderNavGroup(DICT.groupDaily, groupedNav[DICT.groupDaily])}
                {renderNavGroup(customGroupTitle, customNavItems)}
                {renderNavGroup('Interactive Toolkits', groupedNav['Interactive Toolkits'])}
                {renderNavGroup('Resource Libraries', groupedNav['Resource Libraries'])}
             </>
          ) : (
             <>
                {renderNavGroup(DICT.groupDaily, groupedNav[DICT.groupDaily])}
                {renderNavGroup(customGroupTitle, customNavItems)}
                {renderNavGroup('Interactive Toolkits', groupedNav['Interactive Toolkits'])}
                {renderNavGroup('Resource Libraries', groupedNav['Resource Libraries'])}
             </>
          )}
        </nav>

        <div 
            className="p-4 border-t shrink-0 mt-auto flex flex-col gap-2 z-10 relative"
            style={{ borderColor: customStyles.borderColor || 'rgba(255,255,255,0.1)' }}
        >
          {isSuperAdmin && (
            <button 
              onClick={() => navigateTo('super-admin')}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-red-900/20 text-red-400 border border-red-900/50 hover:bg-red-900/40 hover:text-white transition-all mb-2"
            >
              <div className="w-6 h-6 rounded-lg bg-red-600 flex items-center justify-center text-white shadow-md"><Shield size={14} /></div>
              <div className="text-left">
                <p className="text-xs font-bold uppercase tracking-wider">Super Admin</p>
                <p className="text-[10px] opacity-70">Master Control</p>
              </div>
            </button>
          )}

          <div 
            className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-colors w-full ${!isBrandingEnabled ? 'bg-slate-800 hover:bg-slate-700' : 'hover:bg-white/5'}`}
            style={{ backgroundColor: isBrandingEnabled ? customStyles.footerTileBg : undefined, border: isBrandingEnabled ? `1px solid ${customStyles.borderColor}` : undefined }}
            onClick={onOpenAdmin}
          >
            <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold transition-colors"
                style={{ backgroundColor: (isViewAdmin || isHub) ? customStyles.activeBg : customStyles.accentColor }}
            >
              {isViewAdmin ? <Lock size={16} /> : BRAND_CONFIG.defaultInitials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: customStyles.textColor }}>{currentUser?.name || "Guest User"}</p>
              <p className="text-xs truncate opacity-70" style={{ color: customStyles.textColor }}>
                {isAdmin ? (isViewAdmin ? `${DICT.roleExec} Mode` : `Switch to ${DICT.roleExec}`) : (memberSurfaceNav ? memberSurfaceNav.item.label : (surface === 'parent' ? 'Parent Hub' : `${resolveSport(currentOrg).id === 'school' ? 'Student' : DICT.roleUser} Mode`))}
              </p>
            </div>
          </div>

          <button 
            onClick={(e) => { e.stopPropagation(); onLogout(); }}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2 border border-transparent rounded-xl transition-all text-sm font-medium ${!isBrandingEnabled ? 'bg-slate-800 text-slate-400 hover:text-red-400' : 'hover:bg-red-500/10 hover:text-red-400'}`}
            style={{ color: isBrandingEnabled ? customStyles.textColor : undefined, opacity: 0.7 }}
          >
            <LogOut size={16} /> <span>Log Out</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;